import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import * as Sentry from '@sentry/react';
import config from 'Config';
import axios from 'helpers/axios/axios';

type UseRecaptchaType = {
  isLoading: boolean;
  success: boolean | undefined;
  recaptchaToken?: string;
}

export const useGetRecaptchaToken = (enabledProp: boolean, action: string) => {
  const enabled = enabledProp && Boolean(process.env.REACT_APP_RECAPTCHA_SITE_KEY);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    if (window.grecaptcha) {
      setIsScriptLoaded(true);
    } else if (enabled) {
      const checkScriptLoaded = setInterval(() => {
        if (window.grecaptcha) {
          setIsScriptLoaded(true);
          clearInterval(checkScriptLoaded);
        }
      }, 100); // Check every 100ms

      return () => clearInterval(checkScriptLoaded);
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled && isScriptLoaded) {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(
              process.env.REACT_APP_RECAPTCHA_SITE_KEY as string,
              { action }
          );
          setRecaptchaToken(token);
        } catch (error) {
          Sentry.captureException('reCAPTCHA token generation failed');
        }
      });
    }
  }, [enabled, action, isScriptLoaded]);

  return { recaptchaToken, enabled };
};

export const useRecaptcha = ({
  enabled: enabledProp, action, identityId
}: {enabled: boolean, action: string, identityId: string }): UseRecaptchaType => {
  // for local by pass check for SITE_KEY
  const { recaptchaToken, enabled } = useGetRecaptchaToken(enabledProp, action);

  const { isLoading: queryInFlight, data, isError } = useQuery(
    ['recaptcha', recaptchaToken, identityId, action],
    () => axios.get(
      config.API_RECAPTCHA_ENDPOINT,
      {
        params: {
          gRecaptchaResponse: recaptchaToken,
          identityId,
          action
        }
      }
    ),
    { enabled: !!recaptchaToken && enabled && action !== 'submit', retry: false }
  );

  const success = !isError && data?.data?.success;

  if (!enabled) {
    return { isLoading: false, success };
  }

  return { isLoading: !recaptchaToken || queryInFlight, success, recaptchaToken };
};